// The sheets over the window: Get Pro in the app (no price, docs/47 decision 4), the keyboard shortcuts, Settings, a
// new project, the assistant's note, and confirmations (deleting data or the account asks for DELETE to be typed, as
// the apps do). Each is a dialog that keeps the focus inside and closes with Esc.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import packageInfo from "../../package.json";
import { languageChoice, setLanguage, t, type LanguageChoice } from "../i18n";
import { PRO_FEATURES, useWindow, type Sheet } from "./context";
import { splitEmoji } from "./format";
import { Icon } from "./Icon";
import { LegalLinks } from "./legal";
import { MailAddressSheet, MailSettings } from "./Mail";
import { JoinSheet, PeopleSheet } from "./Sharing";
import { UseTemplateSheet } from "./Templates";
import { HabitSettings, MilestoneMoment, PauseHabitSheet } from "./Habits";
import { UpdateSettings } from "./Update";
import { setPrefs, STALE_CHOICES, usePrefs } from "./prefs";

export function Dialog({ title, onClose, children, wide, labelledBy = "sheet-title" }: { title?: string; onClose: () => void; children: ReactNode; wide?: boolean; labelledBy?: string }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>("[autofocus], input, button.mint-button, button");
    first?.focus();
    return () => returnTo?.focus?.({ preventScroll: true });
  }, []);
  const key = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...(panel.current?.querySelectorAll<HTMLElement>("button, input, select, textarea, a[href], [tabindex='0']") ?? [])].filter((element) => !element.hasAttribute("disabled"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return (
    <div className="sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }} onKeyDown={key}>
      <div ref={panel} className={`sheet${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-label={title}>
        {children}
      </div>
    </div>
  );
}

export function Sheets() {
  const { ui, showSheet } = useWindow();
  const sheet = ui.sheet;
  if (!sheet) return null;
  const close = () => showSheet(null);
  switch (sheet.kind) {
    case "pro": return <ProSheet sheet={sheet} onClose={close} />;
    case "keys": return <KeysSheet onClose={close} />;
    case "settings": return <SettingsSheet onClose={close} section={sheet.section} />;
    case "assistant": return <AssistantSheet onClose={close} />;
    case "newProject": return <NewProjectSheet onClose={close} />;
    case "people": return <PeopleSheet route={sheet.route} onClose={close} />;
    case "join": return <JoinSheet invitationID={sheet.invitationID} onClose={close} />;
    case "mail": return <MailAddressSheet kind={sheet.mailKind} targetID={sheet.targetID} label={sheet.label} onClose={close} />;
    case "useTemplate": return <UseTemplateSheet templateID={sheet.templateID} onClose={close} />;
    case "confirm": return <ConfirmSheet sheet={sheet} onClose={close} />;
    case "pauseHabit": return <PauseHabitSheet habitID={sheet.habitID} onClose={close} />;
    case "milestone": return <MilestoneMoment habitID={sheet.habitID} count={sheet.count} onClose={close} />;
  }
}

function ProSheet({ sheet, onClose }: { sheet: Extract<Sheet, { kind: "pro" }>; onClose: () => void }) {
  const feature = PRO_FEATURES[sheet.feature];
  return (
    <Dialog onClose={onClose}>
      <div className="pro-hero"><span className="icon-square"><Icon name={feature.icon} /></span><div><b>{t(feature.title)}</b><span>{t(feature.text)}</span></div></div>
      <h2 id="sheet-title">{t("Part of Todolix Pro")}</h2>
      <p>{t("Get Pro in the Todolix app on your iPhone, iPad or Mac. It works here as soon as it's on, with the same account.")}</p>
      <div className="devices"><span>iPhone</span><span>iPad</span><span>Mac</span></div>
      <div className="actions">
        <button className="mint-button" onClick={onClose}>{t("OK")}</button>
      </div>
    </Dialog>
  );
}

function AssistantSheet({ onClose }: { onClose: () => void }) {
  return (
    <Dialog onClose={onClose}>
      <div className="pro-hero"><span className="icon-square"><Icon name="sparkle" /></span><div><b>{t("The Assistant")}</b><span>{t("Ask about your day, or say what to change.")}</span></div></div>
      <h2 id="sheet-title">{t("Coming to the Web")}</h2>
      <p>{t("The assistant is on your iPhone, iPad and Mac for now. It comes to the web in a later version.")}</p>
      <div className="actions"><button className="mint-button" onClick={onClose}>{t("OK")}</button></div>
    </Dialog>
  );
}

export function isApplePlatform(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

function KeysSheet({ onClose }: { onClose: () => void }) {
  const command = isApplePlatform() ? "⌘" : "Ctrl";
  const option = isApplePlatform() ? "⌥" : "Alt";
  // Only what works here (docs/55, item 21): the web's own keys, those the browser leaves free.
  const rows: ([string] | [string[], string])[] = [
    [t("Moving About")],
    [["↑", "↓"], t("Previous or next to-do")],
    [["⇧", "↑"], t("Select more")],
    [["G", "T"], t("Today (G then I Inbox, H Habits, D Deadlines, S Scheduled, A Anytime, O Someday, R Archive)")],
    [["\\"], t("Show or hide Home")],
    [["O"], t("Show or hide the overview beside the list")],
    [["F"], t("Find")],
    [["?"], t("This list")],
    [t("To-Dos")],
    [["Q"], t("New to-do")],
    [["Return"], t("Open")],
    [["Esc"], t("Close, or clear the selection")],
    [["Space"], t("Complete")],
    [[command, t("Click")], t("Add to the selection")],
    [["⇧", t("Click")], t("Select a range")],
    [[command, "A"], t("Select all")],
    [[t("Delete")], t("Delete")],
    [[option, "→"], t("A day later")],
    [[option, "←"], t("A day earlier")],
    [[command, "Z"], t("Undo")],
    [["⇧", command, "Z"], t("Redo")],
    [t("The Week")],
    [["Y"], t("Scheduled as a week, or as a list")],
    [["←", "→"], t("The previous or next day")],
    [["↑", "↓"], t("Up and down inside a day")],
    [["⇧", "[", "]"], t("A week earlier or later")],
    [["0"], t("This week")],
    [["Q"], t("New to-do on the chosen day")],
    [["P"], t("Show or hide Unscheduled")],
    [["Tab"], t("Into Unscheduled's filter")],
    [t("In an Open To-Do")],
    [["Tab"], t("From the title to the chips, then between them")],
    [["←", "→"], t("Between the chips")],
    [["Return"], t("Open the chip's picker")],
    [["W"], t("When")],
    [["D"], t("Deadline")],
    [[option, command, "↑", "↓"], t("Move a checklist item")],
    [[command, "Return"], t("Save and close")],
    [["Esc"], t("Close the picker, then the to-do")],
    [t("In a Picker")],
    [[t("Type")], t("Narrow the choices, or a day or time: tom, fri, 12.10., 9")],
    [["↑", "↓"], t("Move over what is left")],
    [["Return"], t("Take the highlighted choice")],
  ];
  return (
    <Dialog onClose={onClose} wide>
      <h2 id="sheet-title">{t("Keyboard Shortcuts")}</h2>
      <p>{t("Keys in the browser. ⌘N, ⌘T and ⌘F stay the browser's.")}</p>
      <div className="keys-grid">
        {rows.map((row, index) => (row.length === 1
          ? <h3 key={index}>{row[0]}</h3>
          : (
            <div key={index} className="keys-row">
              <span className="k">{row[0].map((key) => <kbd key={key} className="kbd">{key}</kbd>)}</span>
              <span>{row[1]}</span>
            </div>
          )))}
      </div>
      <div className="actions"><button className="mint-button" onClick={onClose}>{t("Done")}</button></div>
    </Dialog>
  );
}

function SettingsSheet({ onClose, section }: { onClose: () => void; section?: "habits" | undefined }) {
  const { state, model, showSheet } = useWindow();
  useEffect(() => {
    if (section) document.querySelector(`.sheet [data-section="${section}"]`)?.scrollIntoView({ block: "start" });
  }, [section]);
  const [language, setChoice] = useState<LanguageChoice>(languageChoice());
  const user = state.user;
  const provider = user?.provider === "apple" ? t("Signed in with Apple") : user?.provider === "google" ? t("Signed in with Google") : t("Signed in with email");
  const confirmTyped = (title: string, text: string, action: string, run: () => Promise<void>) =>
    showSheet({ kind: "confirm", title, text, action, run, typed: true });
  return (
    <Dialog onClose={onClose} wide>
      <h2 id="sheet-title">{t("Settings")}</h2>
      <section className="settings-group" aria-label={t("Account")}>
        <h3>{t("Account")}</h3>
        <div className="settings-row">
          <div><b>{user?.name ?? user?.email}</b><span>{user?.email} · {provider}</span></div>
          <button className="outline-button small" onClick={() => { onClose(); void model.auth.signOut(); }}><Icon name="signOut" />{t("Sign Out")}</button>
        </div>
        <div className="settings-row">
          <div><b>{t("Todolix Pro")}</b><span>{state.isPro ? t("On, with this account.") : t("Get Pro in the Todolix app on your iPhone, iPad or Mac.")}</span></div>
        </div>
      </section>
      <section className="settings-group">
        <h3>{t("Language")}</h3>
        <div className="segmented" role="radiogroup" aria-label={t("Language")}>
          {(["system", "en", "de"] as const).map((choice) => (
            <button key={choice} role="radio" aria-checked={language === choice} className={language === choice ? "on" : ""}
              onClick={() => { setChoice(choice); setLanguage(choice); }}>
              {choice === "system" ? t("Browser") : choice === "en" ? "English" : "Deutsch"}
            </button>
          ))}
        </div>
      </section>
      <ListsSettings />
      <HabitSettings />
      <MailSettings />
      <UpdateSettings />
      <section className="settings-group">
        <h3>{t("Situations")}</h3>
        <p className="settings-note"><Icon name="info" />{t("The web shows everything: situations hide to-dos on your iPhone, iPad and Mac, not here.")}</p>
      </section>
      <section className="settings-group">
        <h3>{t("Data")}</h3>
        <div className="settings-actions">
          <button className="outline-button small destructive" onClick={() => confirmTyped(t("Delete All Data?"),
            t("Every to-do, project, area and section goes, on all your devices. Your account stays."), t("Delete All Data"), () => model.deleteAllData())}>
            {t("Delete All Data")}
          </button>
          <button className="outline-button small destructive" onClick={() => confirmTyped(t("Delete Account?"),
            t("Your account and everything in it go, on all your devices. A subscription is managed in the App Store."), t("Delete Account"), () => model.auth.deleteAccount())}>
            {t("Delete Account")}
          </button>
        </div>
      </section>
      <p className="settings-version">{t("Todolix for the web, version {version}", { version: packageInfo.version })}</p>
      <LegalLinks className="settings-legal" />
      <p className="settings-legal">{t("Sound Effect by José Mejía from Pixabay")}</p>
      <p className="settings-legal">{t("Sound Effect by SoundShelfStudio from Pixabay")}</p>
      <div className="actions"><button className="mint-button" onClick={onClose}>{t("Done")}</button></div>
    </Dialog>
  );
}

function ConfirmSheet({ sheet, onClose }: { sheet: Extract<Sheet, { kind: "confirm" }>; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  const needsTyping = sheet.typed === true;
  const word = t("DELETE");
  const ready = !needsTyping || typed.trim().toUpperCase() === word.toUpperCase();
  const cancel = () => { onClose(); sheet.onCancel?.(); };
  return (
    <Dialog onClose={cancel}>
      <h2 id="sheet-title">{sheet.title}</h2>
      <p>{sheet.text}</p>
      {needsTyping && (
        <label className="confirm-field">
          <span>{t("Type {word} to confirm.", { word })}</span>
          <input className="field" value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" autoFocus aria-label={t("Type {word} to confirm.", { word })} />
        </label>
      )}
      <div className="actions">
        <button className="outline-button" onClick={cancel}>{sheet.cancel ?? t("Cancel")}</button>
        <button className={`mint-button${sheet.quiet ? "" : " destructive"}`} disabled={!ready} onClick={() => { onClose(); void sheet.run(); }}>{sheet.action}</button>
      </div>
    </Dialog>
  );
}

function NewProjectSheet({ onClose }: { onClose: () => void }) {
  const { state, model, go } = useWindow();
  const areas = state.lists.sortedAreas;
  const [title, setTitle] = useState("");
  const [areaID, setAreaID] = useState(areas[0]?.id ?? "");
  const [kind, setKind] = useState<"project" | "list">("project");
  const create = () => {
    if (!title.trim() || !areaID) return;
    const project = model.addProject(title.trim(), areaID, kind);
    onClose();
    go({ kind: "project", id: project.id });
  };
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{kind === "list" ? t("New List") : t("New Project")}</h2>
      <p>{t("A project has an end; a list goes on.")}</p>
      <div className="segmented" role="radiogroup" aria-label={t("Kind")}>
        <button role="radio" aria-checked={kind === "project"} className={kind === "project" ? "on" : ""} onClick={() => setKind("project")}>{t("Project")}</button>
        <button role="radio" aria-checked={kind === "list"} className={kind === "list" ? "on" : ""} onClick={() => setKind("list")}>{t("List")}</button>
      </div>
      <input className="field" value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t("Name")} aria-label={t("Name")} autoFocus
        onKeyDown={(event) => { if (event.key === "Enter") create(); }} />
      {areas.length > 0 ? (
        <select className="field" value={areaID} onChange={(event) => setAreaID(event.target.value)} aria-label={t("Area")}>
          {areas.map((area) => <option key={area.id} value={area.id}>{[splitEmoji(area.title).emoji, splitEmoji(area.title).name].filter(Boolean).join(" ")}</option>)}
        </select>
      ) : <p>{t("Make an area in the app first.")}</p>}
      <div className="actions">
        <button className="outline-button" onClick={onClose}>{t("Cancel")}</button>
        <button className="mint-button" disabled={!title.trim() || !areaID} onClick={create}>{t("Create")}</button>
      </div>
    </Dialog>
  );
}

/** Settings › Lists: whether Today keeps its empty sections, which of Someday, Stale and the Archive Home shows (Pro), when a to-do is stale, the sound of
 * ticking one off (docs/59), and done checklist items at the end (as the apps' Settings › Lists). In this browser. */
function ListsSettings() {
  const { state, showSheet } = useWindow();
  const chosen = usePrefs();
  const lists = [["someday", t("Someday")], ["stale", t("Stale")], ["archive", t("Archive")]] as const;
  return (
    <section className="settings-group" aria-label={t("Lists")}>
      <h3>{t("In Today")}</h3>
      <div className="toggles">
        <label className="toggle-row">
          <span>{t("Show Empty Sections")}</span>
          <input type="checkbox" role="switch" checked={chosen.todayShowsEmptySections}
            onChange={(event) => { const value = event.target.checked; setPrefs((prefs) => ({ ...prefs, todayShowsEmptySections: value })); }} />
        </label>
      </div>
      <p className="settings-note">{t("Your sections in Today show even when nothing is left in them. When this is off, an empty section shows again once a to-do is in it.")}</p>
      <h3>{t("On Home")}</h3>
      <div className="toggles">
        {lists.map(([kind, label]) => {
          const on = !state.isPro || chosen.homeShows[kind];
          return (
            <label key={kind} className="toggle-row">
              <span>{label}</span>
              <input type="checkbox" role="switch" checked={on}
                onChange={(event) => {
                  if (!state.isPro) return showSheet({ kind: "pro", feature: "homeLists" });
                  const value = event.target.checked;
                  setPrefs((prefs) => ({ ...prefs, homeShows: { ...prefs.homeShows, [kind]: value } }));
                }} />
            </label>
          );
        })}
      </div>
      <p className="settings-note">{t("Inbox, Today and Deadlines always stay at the top of Home. Lists left off Home are always in Find.")}</p>
      <h3>{t("Stale After")}</h3>
      <div className="segmented" role="radiogroup" aria-label={t("Stale After")}>
        {STALE_CHOICES.map((days) => (
          <button key={days} role="radio" aria-checked={chosen.staleAfterDays === days} className={chosen.staleAfterDays === days ? "on" : ""}
            onClick={() => setPrefs((prefs) => ({ ...prefs, staleAfterDays: days }))}>
            {t("{count} Days", { count: days })}
          </button>
        ))}
      </div>
      <p className="settings-note">{t("Open to-dos without a date or deadline that were added this long ago. They turn brown in lists and gather in Stale.")}</p>
      <h3>{t("Completing To-Dos")}</h3>
      <div className="toggles">
        <label className="toggle-row">
          <span>{t("Sound When Completing")}</span>
          <input type="checkbox" role="switch" checked={chosen.completionSound}
            onChange={(event) => { const value = event.target.checked; setPrefs((prefs) => ({ ...prefs, completionSound: value })); }} />
        </label>
      </div>
      <p className="settings-note">{t("A short, soft sound as you tick off a to-do.")}</p>
      <h3>{t("Checklists")}</h3>
      <div className="toggles">
        <label className="toggle-row">
          <span>{t("Done Items at the End")}</span>
          <input type="checkbox" role="switch" checked={chosen.checklistDoneLast}
            onChange={(event) => { const value = event.target.checked; setPrefs((prefs) => ({ ...prefs, checklistDoneLast: value })); }} />
        </label>
      </div>
      <p className="settings-note">{t("A ticked item moves below the open ones. Opened again, it goes back to its place.")}</p>
    </section>
  );
}
